// SharedSecrets list component for platform-wide Zenky account credentials
import {
    CreateButton,
    Datagrid,
    DateField,
    List,
    TextField,
    TopToolbar,
    useRecordContext,
    BooleanField,
} from 'react-admin';
import { Chip } from '@mui/material';
import type { SharedSecrets } from '../../types/secret';

const ListActions = () => (
    <TopToolbar>
        <CreateButton />
    </TopToolbar>
);

// Custom field to show expiration warnings
const ExpirationField = ({ source, label: _label }: { source: string; label: string }) => {
    const record = useRecordContext<SharedSecrets>();
    if (!record) return null;

    const value = record[source as keyof SharedSecrets];
    if (!value || typeof value !== 'string') return <span>-</span>;

    const expirationDate = new Date(value);
    const now = new Date();
    const daysUntilExpiration = Math.floor((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    let color: 'error' | 'warning' | 'default' = 'default';
    if (daysUntilExpiration < 0) {
        color = 'error';
    } else if (daysUntilExpiration < 30) {
        color = 'warning';
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DateField source={source} showTime={false} />
            {daysUntilExpiration < 30 && (
                <Chip
                    label={daysUntilExpiration < 0 ? 'Expired' : `${daysUntilExpiration} days`}
                    color={color}
                    size="small"
                />
            )}
        </div>
    );
};

ExpirationField.defaultProps = { label: 'Expiration' };

export const SharedSecretsList = () => (
    <List
        actions={<ListActions />}
        pagination={false}
        perPage={1}
        title="Shared Zenky Account Secrets"
    >
        <Datagrid rowClick="edit" bulkActionButtons={false}>
            {/* iOS Section */}
            <BooleanField source="hasIosCertificate" label="iOS Certificate" />
            <ExpirationField source="certificateExpiration" label="Cert Expiration" />
            <TextField source="teamName" label="Team Name" />
            <BooleanField source="autoManageCertificates" label="Auto-Manage" />

            {/* Publishing */}
            <BooleanField source="hasPlayStoreCredentials" label="Google Play" />
            <BooleanField source="hasAppStoreConnectKey" label="App Store" />

            {/* Metadata */}
            <DateField source="updatedAt" showTime />
        </Datagrid>
    </List>
);
